<template>
  <div class="d-flex flex-column" style="height: 100vh;">
     <!-- Header and Actions -->
     <div class="px-4 px-md-8 pt-8 pb-2 flex-shrink-0">
        <div class="d-flex justify-space-between align-center mb-2">
            <div class="text-h5 font-weight-bold">
                {{ navObject.editmode ? $t("edit_user") : $t("create_user") }}
            </div>
            <v-btn
                text
                fab
                small
                @click="close()"
                :ripple="false"
            >
                <img width='24' height='24' src="../../assets/img/svg/icons/close.svg" alt="close">
            </v-btn>
        </div>
     </div>

     <!-- Scrollable Form Content -->
     <div class="flex-grow-1 overflow-y-auto px-4 px-md-8">
        <v-form ref="form" class="mt-2">
            
            <div class="text-h6 mb-3 font-weight-medium grey--text text--darken-2">
                {{ $t("user_info") }}
            </div>

            <v-row dense>
                <v-col cols="12" md="6">
                    <v-text-field
                        :readonly="!navObject.editmode && !navObject.createmode"
                        v-model="editUser.user.personalData.firstname"
                        :label="$t('firstname')"
                        outlined
                        dense
                        :rules="rules"
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                    <v-text-field
                        :readonly="!navObject.editmode && !navObject.createmode"
                        v-model="editUser.user.personalData.lastname"
                        :label="$t('lastname')"
                        outlined
                        dense
                        :rules="rules"
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                </v-col>

                <v-col cols="12">
                     <v-text-field
                        :readonly="!navObject.editmode && !navObject.createmode"
                        v-model="editUser.user.mail"
                        :label="$t('email')"
                        outlined
                        dense
                        :rules="emailRules"
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                </v-col>

                <v-col cols="12" v-if="!isTenantAdmin()">
                    <v-autocomplete
                        :readonly="!navObject.editmode && !navObject.createmode"
                        :items="tenantItems"
                        v-model="editUser.user.customer"
                        :label="$t('tenant')"
                        outlined
                        dense
                        item-value="id"
                        item-text="name"
                        return-object
                        hide-details="auto"
                        class="rounded-0 mb-2"
                        :no-filter="true"
                        :loading="tenantLoading"
                        :search-input.sync="tenantSearch"
                        :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                        @focus="ensureTenantsLoaded()"
                    >
                        <template v-slot:append-item>
                                <div
                                v-if="tenantHasMore"
                                style="height: 1px;"
                                v-intersect="onTenantListIntersect"
                                />
                                <div v-else class="px-4 py-2 grey--text text--darken-1 text-caption">
                                {{ tenantItems.length ? $t('no_more_results') : $t('no_results') }}
                                </div>
                        </template>
                    </v-autocomplete>
                </v-col>

                <template v-if="shouldShowRoleField">
                    <v-col cols="12">
                        <v-autocomplete
                            :readonly="!navObject.editmode && !navObject.createmode"
                            :items="filteredRoleNames"
                            v-model="editUser.user.role"
                            :label="$t('role')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                            :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                        ></v-autocomplete>
                    </v-col>
                </template>
            </v-row>

            <template v-if="navObject.editmode">
                <v-divider class="my-6"></v-divider>
                <div class="text-h6 mb-3 font-weight-medium grey--text text--darken-2">
                    {{ $t("password_change") }}
                </div>
                
                 <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-show="false"
                        ></v-text-field>
                         <v-text-field
                            :readonly="!navObject.editmode && !navObject.createmode"
                            v-model="editUser.user.password"
                            :label="$t('new_password')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                            :type="textfieldtype ? 'text':'password'"
                            :append-icon="textfieldtype ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="textfieldtype = !textfieldtype"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                         <v-text-field
                            :readonly="!navObject.editmode && !navObject.createmode"
                            v-model="editUser.user.confirmPassword"
                            :label="$t('confirm_password')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                            :type="textfieldtype ? 'text':'password'"
                            :append-icon="textfieldtype ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="textfieldtype = !textfieldtype"
                        ></v-text-field>
                    </v-col>
                </v-row>
            </template>

            <template v-if="navObject.createmode">
                <v-divider class="my-6"></v-divider>
                 <v-alert dense text type="info" class="mb-4">
                    {{ $t('user_invitation_info') }}
                </v-alert>

                <v-row dense>
                    <v-col cols="12">
                        <v-select
                            :items="languageOptions"
                            v-model="selectedLanguage"
                            :label="$t('email_language')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                            :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" class="mt-n2">
                        <v-checkbox
                            v-model="sendNotificationMail"
                            :label="$t('send_notification_to_me')"
                            hide-details
                            class="mt-0 pt-0"
                        ></v-checkbox>
                    </v-col>
                </v-row>
            </template>

            <v-row dense v-if="!hasRight('GlobalAccess') && editUser.user.deviceIdentifierContainer.deviceIdentifiers.length > 0" class="mt-4">
                 <v-col cols="12">
                    <div class="text-subtitle-1 mb-2">
                         {{ $t("devices") }} ({{editUser.user.deviceIdentifierContainer.deviceIdentifiers.length}} / {{ editUser.user.deviceIdentifierContainer.maxDevices }})
                    </div>
                     <v-simple-table class="devicetable">
                        <thead>
                        <tr>
                            <th>{{ $t("device") }}</th>
                            <th class="text-right">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="device in editUser.user.deviceIdentifierContainer.deviceIdentifiers" :key="device">
                            <td>{{ device }}</td>
                            <td class="text-right">
                                <v-btn
                                    icon
                                    color="error"
                                    small
                                    @click="removeDevice(editUser.user.id, device)"
                                >
                                    <i class="fas fa-trash-alt"></i>
                                </v-btn>
                            </td>
                        </tr>
                        </tbody>
                    </v-simple-table>
                 </v-col>
            </v-row>

        </v-form>
     </div>

     <!-- Footer Actions -->
     <div class="px-4 px-md-8 pt-4 pb-8 flex-shrink-0">
        <v-btn
          v-if="navObject.createmode"
          class="white--text"
          color="#161616"
          tile
          large
          block
          depressed
          @click="createUser()"
        >
          {{ $t("create_new_user") }}
        </v-btn>
        <v-btn
          v-if="navObject.editmode"
          class="white--text"
          color="#161616"
          tile
          large
          block
          depressed
          @click="editingUser()"
        >
          {{ $t("save_changes") }}
        </v-btn>
     </div>
  </div>
</template>

<script>
export default {

    props: {
    navObject: Object,
    editUser: Object,
    metadata: Object,
    close: Function,
    reload: Function,
    removeDevice: Function,
    },

    data() {
        return {
            dots: 6,
            routes: {
                users: "api/users",
            },
            emailRules: [
            (v) => !!v || this.$t("email_required"),
            (v) =>
            /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
            this.$t("email_valid"),
            ],
            rules: [(value) => !!value || this.$t("required")],
            textfieldtype: false,
            me: null,
            isEditingOwnUser: false,
            selectedLanguage: null,
            sendNotificationMail: true,
            languageOptions: [
                { text: 'Deutsch', value: 'de' },
                { text: 'English', value: 'en' }
            ],
            tenantItems: [],
            tenantLoading: false,
            tenantSearch: "",
            tenantPage: 0,
            tenantSize: 8,
            tenantTotalPages: 1,
            tenantHasMore: true,

            tenantSearchDebounceTimer: null,
            lastTenantQueryKey: null,
        }
    },

    mounted() {
        this.fetchMe();
        // Initialize language selector with current user's language (or browser language as fallback)
        this.selectedLanguage = this.$getBrowserLanguage();
    },

    watch: {
        'navObject.createmode'(isCreateMode) {
            if (isCreateMode) {
                this.sendNotificationMail = true;
                if (this.me && this.me.language) {
                    this.selectedLanguage = this.me.language;
                }
            }
        },
        tenantSearch(newVal) {
            const search = (newVal ?? "").toString();

            clearTimeout(this.tenantSearchDebounceTimer);
            this.tenantSearchDebounceTimer = setTimeout(() => {
            if (search.trim().length > 0 && search.trim().length < 2) return;

            this.resetTenantPagination();
            this.fetchTenantsPage();
            }, 300);
        },
        filteredRoleNames: {
            handler(newRoles) {
                // Auto-select default "User" role when creating a new user
                if (this.navObject.createmode && newRoles && newRoles.length > 0 && !this.editUser.user.role) {
                    // Find the role named "User" or "Benutzer"
                    const defaultRole = newRoles.find(roleItem =>
                        roleItem.text === 'User' ||
                        roleItem.text === 'Benutzer' ||
                        roleItem.value?.name === 'User' ||
                        roleItem.value?.name === 'Benutzer'
                    );

                    if (defaultRole) {
                        this.editUser.user.role = defaultRole.value;
                    } else if (newRoles.length > 0) {
                        // If no "User" role found, select the first available role
                        this.editUser.user.role = newRoles[0].value;
                    }
                }
            },
            immediate: true
        }
    },

    computed: {
        shouldShowRoleField() {
            // Don't show role field when user edits their own profile
            return !this.isEditingOwnUser;
        },
        filteredRoleNames() {
            // Filter roles for Tenant Admins
            if (!this.isTenantAdmin()) {
                // Global Admins can assign all roles
                return this.metadata.roleNames;
            }

            // Tenant Admins can only assign roles without GlobalAccess right
            return this.metadata.roleNames.filter(roleItem => {
                const role = roleItem.value;
                if (!role || !role.rights) return true;
                // Exclude roles that have GlobalAccess or ChangePermissions
                return !role.rights.includes('GlobalAccess') &&
                       !role.rights.includes('ChangePermissions');
            });
        }
    },

    methods: {
        async fetchMe() {
            var _this = this;
            await this.$axios
                .get("api/me")
                .then(function (response) {
                    _this.me = response.data;

                    // Check if editing own profile to prevent layout shift
                    _this.isEditingOwnUser = _this.navObject.editmode &&
                                             _this.editUser.user &&
                                             _this.me.id === _this.editUser.user.id;

                    // Auto-assign tenant for Tenant Admin when creating users
                    if (_this.isTenantAdmin() && _this.navObject.createmode && _this.me.customer != null) {
                        _this.editUser.user.customer = _this.me.customer;
                    }

                    // Set default language for invitation to current user's language
                    if (_this.navObject.createmode && _this.me.language) {
                        _this.selectedLanguage = _this.me.language;
                    }

                    _this.ensureSelectedTenantInItems();
                })
                .catch(this.onError);
        },
        resetTenantPagination() {
            const selected = this.editUser?.user?.customer;
            this.tenantItems = [];
            this.tenantPage = 0;
            this.tenantTotalPages = 1;
            this.tenantHasMore = true;

            if (selected?.id) this.tenantItems.unshift(selected);
        },
        ensureTenantsLoaded() {
            if (!this.tenantItems.length && !this.tenantLoading) {
                this.resetTenantPagination();
                this.fetchTenantsPage();
            }
        },
        ensureSelectedTenantInItems() {
            const selected = this.editUser?.user?.customer;
            if (!selected || !selected.id) return;

            const exists = this.tenantItems.some(t => t.id === selected.id);
            if (!exists) {
                this.tenantItems.unshift(selected);
            }
        },
        async fetchTenantsPage() {
            if (this.tenantLoading) return;
            if (!this.tenantHasMore) return;

            const search = (this.tenantSearch ?? "").toString().trim();
            const queryKey = `${search}::${this.tenantPage}`;
            this.lastTenantQueryKey = queryKey;

            this.tenantLoading = true;
            try {
                let url;
                const params = { page: this.tenantPage, size: this.tenantSize };

                if (search.length > 0) {
                url = `api/customers/search/${encodeURIComponent(search)}`;
                } else {
                url = `api/customers`;
                }

                const res = await this.$axios.get(url, { params });

                if (this.lastTenantQueryKey !== queryKey) return;

                const content = res.data?.content || [];
                const totalPages = res.data?.totalPages ?? 1;

                const existing = new Set(this.tenantItems.map(t => t.id));
                for (const t of content) {
                if (!existing.has(t.id)) this.tenantItems.push(t);
                }

                this.tenantTotalPages = totalPages;

                this.tenantPage += 1;
                this.tenantHasMore = this.tenantPage < this.tenantTotalPages;
            } catch (err) {
                this.onError(err);
            } finally {
                this.tenantLoading = false;
            }
        },
        onTenantListIntersect(entries, observer, isIntersecting) {
            if (isIntersecting) {
                this.fetchTenantsPage();
            }
        },
        isTenantAdmin() {
            if (!this.me) return false;
            // A Tenant Admin has ManageTenantUsers or ManageTenant rights but NOT GlobalAccess
            const hasManageRights = this.me.rights.includes("ManageTenantUsers") ||
                                   this.me.rights.includes("ManageTenant") ||
                                   (this.me.role && (this.me.role.rights.includes("ManageTenantUsers") ||
                                                     this.me.role.rights.includes("ManageTenant")));
            const hasGlobalAccess = this.me.rights.includes("GlobalAccess") ||
                                   (this.me.role && this.me.role.rights.includes("GlobalAccess"));
            return hasManageRights && !hasGlobalAccess;
        },
        onError(err) {
        if (err.response != null && err.response.data != null) {
            this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t(err.response.data.message));
        } else {
            console.error(err);
        }
        },
        createUser() {
        var _this = this;

        var saveUser = {};
        Object.assign(saveUser, this.editUser.user);

        // Tenant Admin users can only create users in their own tenant
        if (_this.isTenantAdmin() && _this.me.customer != null) {
            saveUser.customer = _this.me.customer;
        }

        saveUser.tenantId = saveUser.customer != null ? saveUser.customer.id : -1;
        saveUser.username = saveUser.mail;
        // Set empty passwords to trigger invitation email
        saveUser.password = "";
        saveUser.confirmPassword = "";
        // Set language for invitation email
        saveUser.language = this.selectedLanguage || 'de';
        saveUser.sendNotificationMail = this.sendNotificationMail;
        saveUser.roleId = this.editUser.user.role && this.editUser.user.role.id ? this.editUser.user.role.id : null;

        this.$axios
            .post(this.routes.users, saveUser)
            .then(function (response) {
            _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("user_created"));
            var user = response.data;
            // Only assign role, not individual rights
            if (_this.editUser.user.role != null && _this.editUser.user.role.id != null) {
                _this.addRoleToUser(user.id, _this.editUser.user.role.id);
            } else {
                // If no role assigned, still need to reload/close
                _this.reload();
                _this.close();
            }
            })
            .catch(this.onError);
        },
        editingUser() {
        var _this = this;

        var saveUser = {};
        Object.assign(saveUser, this.editUser.user);

        saveUser.tenantId = saveUser.customer != null ? saveUser.customer.id : -1;
        saveUser.username = saveUser.mail;

        this.$axios
            .put(this.routes.users, saveUser)
            .then(function (response) {
            var user = response.data;
            _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("user_updated"));

            // Only update role if not editing own profile (never update individual rights)
            if (!_this.isEditingOwnUser) {
                if (_this.editUser.user.role != null && _this.editUser.user.role.id != null) {
                    _this.addRoleToUser(user.id, _this.editUser.user.role.id);
                } else {
                    _this.reload();
                    _this.close();
                }
            } else {
                // If editing own profile, just reload and close
                _this.reload();
                _this.close();
            }
            })
            .catch(this.onError);
        },
        addRoleToUser(userid, roleid) {
            if(roleid == null) return;
            var _this = this;
            this.$axios
                .put("api/users/" + userid + "/role", roleid, this.getJsonOptions())
                .then(function (response) {
                    _this.reload();
                    _this.close();
                })
                .catch(this.onError);
        },
        hasRight(right) {
            if(this.editUser.createmode == true) return false;
            if (this.editUser.user.rights.includes(right)) return true;
            if (this.editUser.user.role != null) {
            return this.editUser.user.role.rights.includes(right);
            }
            return false;
        },
        getJsonOptions() {
        const options = {
            headers: {
            "Content-Type": "application/json",
            },
        };
        return options;
        },
    },

}
</script>


<style scoped>
.v-text-field--outlined >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}

.v-autocomplete.v-text-field--outlined >>> .v-label--active,
.v-select.v-text-field--outlined >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}

.v-text-field--outlined.v-text-field--dense >>> .v-label {
  top: 14px;
}

.v-text-field--outlined.v-text-field--dense >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}
</style>
